import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService, AnalysisResults, MapType, PairRow, RefRow } from '../services/apiService';
import MapVisualization from '../components/MapVisualization';
import {
  MethodologyPanel,
  PairTable,
  RefTable,
  formatHa,
  formatPercent,
  highestRisk,
  matchesFilter,
  pairCode,
  pairLabel,
  pairSeverity,
  refOverlapHa,
  refPlotCode,
  refRisk,
  riskClass,
} from '../components/AnalysisTables';
import './Analysis.css';

const MAPS: { type: MapType; title: string; description: string }[] = [
  {
    type: 'all-layers',
    title: 'All Layers',
    description: 'Combined map with toggleable layers: plots, forest reserves, protected areas, forest cover and overlaps.',
  },
  {
    type: 'overlapping-plots',
    title: 'Plot-to-Plot Overlaps',
    description: 'Visualize areas where rubber plots overlap with each other, indicating potential land claim conflicts.',
  },
  {
    type: 'forest-reserve-overlap',
    title: 'Forest Reserve Overlaps',
    description: 'Shows overlaps with Thai National Reserved Forests, which may indicate forestry regulation compliance issues.',
  },
  {
    type: 'protected-area-overlap',
    title: 'Protected Area Overlaps',
    description: 'Displays overlaps with Thai Protected Areas, highlighting potential conservation regulation conflicts.',
  },
  {
    type: 'forest-cover-overlap',
    title: 'Forest Cover Overlaps',
    description: 'Shows overlaps with JRC Global Forest Cover 2020 (V2) data, relevant for EUDR deforestation analysis.',
  },
];

const Analysis: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [analysisResults, setAnalysisResults] = useState<AnalysisResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState(searchParams.get('customer_id') || '');
  const [customerIdValid, setCustomerIdValid] = useState<boolean | null>(null);
  const [validatingCustomer, setValidatingCustomer] = useState(false);
  const [creditInfo, setCreditInfo] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authenticatedCustomerId, setAuthenticatedCustomerId] = useState<string>('');
  const [plotFilter, setPlotFilter] = useState('');
  const [mapFocus, setMapFocus] = useState<Partial<Record<MapType, string>>>({});
  const [mapOpenRequest, setMapOpenRequest] = useState<Partial<Record<MapType, number>>>({});

  const showOnMap = (mapType: MapType, plotCode: string) => {
    setMapFocus((prev) => ({ ...prev, [mapType]: plotCode }));
    setMapOpenRequest((prev) => ({ ...prev, [mapType]: (prev[mapType] || 0) + 1 }));
  };

  const loadAnalysisResultsAuthenticated = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading authenticated analysis results...');
      const results = await apiService.getAnalysisResultsAuthenticated();
      console.log('Received results:', results ? 'Data found' : 'No data');
      setAnalysisResults(results);
      if (!results) {
        setError('No analysis data available. Upload GeoJSON files first to run overlap analysis.');
      }
    } catch (err) {
      setError('Failed to load analysis results');
      console.error('Error loading analysis results:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      const authenticated = apiService.isAuthenticated();
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        try {
          // Get authenticated user's data to extract customer_id
          const userData = apiService.getStoredUser();
          if (userData?.customer_id) {
            setAuthenticatedCustomerId(userData.customer_id);
          }
          
          // Auto-load results for authenticated users
          loadAnalysisResultsAuthenticated();
        } catch (error) {
          console.error('Error getting authenticated user data:', error);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const loadAnalysisResults = async () => {
    const currentCustomerId = customerId.trim().toUpperCase();
    if (!currentCustomerId) {
      setError('Please enter a Customer ID first');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const results = await apiService.getAnalysisResults(currentCustomerId);
      setAnalysisResults(results);
    } catch (err) {
      setError('Failed to load analysis results');
      console.error('Error loading analysis results:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateCustomerId = async () => {
    if (!customerId.trim()) {
      setCustomerIdValid(false);
      setCreditInfo(null);
      return;
    }

    setValidatingCustomer(true);
    try {
      const result = await apiService.validateCustomer(customerId.trim().toUpperCase());
      setCustomerIdValid(result.valid);
      
      if (result.valid) {
        // Get credit information
        try {
          const credits = await apiService.getCreditBalance(customerId.trim().toUpperCase());
          setCreditInfo(credits);
        } catch (creditError) {
          console.error('Error getting credit info:', creditError);
        }
      }
    } catch (error) {
      setCustomerIdValid(false);
      setCreditInfo(null);
    } finally {
      setValidatingCustomer(false);
    }
  };

  if (loading) {
    return (
      <div className="analysis-page">
        <div className="loading">
          <div className="spinner"></div>
          <span>Loading analysis results...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="analysis-page">
      <h1>Proven Analysis Results</h1>
      <p className="page-description">
        EUDR compliance analysis showing overlaps with forest reserves, protected areas, and forest cover data.
      </p>

      {/* Customer ID Section for Manual Analysis (only show for non-authenticated users) */}
      {!isAuthenticated && (
        <div className="customer-section">
          <h3>Customer Information</h3>
        <div className="customer-input">
          <input
            type="text"
            placeholder="Enter Customer ID"
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setCustomerIdValid(null);
            }}
            onBlur={validateCustomerId}
            className={`customer-id-input ${
              customerIdValid === true ? 'valid' : 
              customerIdValid === false ? 'invalid' : ''
            }`}
          />
          <button 
            onClick={validateCustomerId}
            disabled={validatingCustomer || !customerId.trim()}
            className="validate-btn"
          >
            {validatingCustomer ? 'Validating...' : 'Validate'}
          </button>
        </div>
        {customerIdValid === true && (
          <div className="validation-message success">✓ Valid Customer ID</div>
        )}
        {customerIdValid === false && (
          <div className="validation-message error">✗ Invalid Customer ID</div>
        )}
        
        {/* Credit Information */}
        {creditInfo && (
          <div className="credit-info">
            <h4>Credit Balance</h4>
            <div className="credit-details">
              <div className="credit-item">
                <span>Current Credits:</span>
                <span className="credit-value">{creditInfo.current_credits}</span>
              </div>
              <div className="credit-item">
                <span>Analyses Available:</span>
                <span className="credit-value">{creditInfo.analyses_available}</span>
              </div>
              <div className="credit-item">
                <span>Cost per Analysis:</span>
                <span className="credit-value">50 credits ($5.00)</span>
              </div>
            </div>
            
            {creditInfo.current_credits < 50 && (
              <div className="credit-warning">
                <p><strong>⚠</strong> Insufficient credits for analysis. You need 50 credits minimum.</p>
                <a
                  href={`/dashboard`}
                  className="btn purchase-btn"
                >
                  Purchase Credits
                </a>
                <a
                  href={`/dashboard`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn secondary"
                >
                  View Dashboard
                </a>
              </div>
            )}
          </div>
        )}
        </div>
      )}

      {error && (
        <div className="error-message">
          <span>✗ {error}</span>
        </div>
      )}

      {!analysisResults && !loading && !error && (
        <div className="no-data">
          <div className="no-data-content">
            <div className="no-data-icon">📊</div>
            <h3>No Analysis Data Available</h3>
            <p>Upload GeoJSON files first to run overlap analysis.</p>
            <div className="no-data-actions">
              <button 
                onClick={() => window.location.href = '/upload'} 
                className="btn primary"
              >
                Go to Upload
              </button>
            </div>
          </div>
        </div>
      )}

      {analysisResults && (() => {
        const r = analysisResults;
        const mapCustomerId = isAuthenticated ? authenticatedCustomerId : customerId.trim().toUpperCase();
        const filter = plotFilter.trim().toLowerCase();
        const tolerance = r.methodology?.min_overlap_m2;
        const toleranceText = tolerance !== undefined ? `${tolerance} m²` : 'the sliver tolerance';

        const pairs = r.overlapping_plots || [];
        const touches = r.boundary_touches || [];
        const forestRows = r.forest_overlap_list || [];
        const protectedRows = r.protected_overlap_list || [];
        const coverRows = r.forest_cover_overlap_list || [];
        const noCoverage = r.forest_cover_no_coverage || [];

        const pairMatches = (row: PairRow) =>
          matchesFilter(pairCode(row, 1) || row.plot1_producer, filter) ||
          matchesFilter(pairCode(row, 2) || row.plot2_producer, filter);
        const refMatches = (row: RefRow) => matchesFilter(refPlotCode(row) || row.producer_name, filter);

        const shownPairs = pairs.filter(pairMatches);
        const shownTouches = touches.filter(pairMatches);
        const shownForest = forestRows.filter(refMatches);
        const shownProtected = protectedRows.filter(refMatches);
        const shownCover = coverRows.filter(refMatches);

        const plotCount = r.plot_count ?? r.plot_summary?.length;
        const totalArea =
          r.total_area_hectares ??
          (r.plot_summary && r.plot_summary.length > 0
            ? r.plot_summary.reduce((s, p) => s + (p.computed_area_hectares || 0), 0)
            : undefined);

        const countLabel = (shown: number, total: number) =>
          filter && shown !== total ? `${shown} of ${total}` : `${total}`;

        const uniqueCount = (codes: (string | undefined)[]) =>
          new Set(codes.filter((c): c is string => !!c)).size;

        const sumHa = (values: (number | undefined)[]) => values.reduce<number>((s, v) => s + (v || 0), 0);

        const categoryRows = [
          {
            key: 'plot',
            icon: '●',
            label: 'Plot-to-plot overlap',
            plots: uniqueCount(pairs.flatMap((p) => [pairLabel(p, 1), pairLabel(p, 2)])),
            ha: sumHa(pairs.map((p) => p.overlap_area)),
            pct: r.overall_overlap_percent,
            risk: highestRisk(pairs.map(pairSeverity)),
            error: undefined as string | undefined,
          },
          {
            key: 'forest_reserve',
            icon: '▬',
            label: 'Forest reserve',
            plots: uniqueCount(forestRows.map((x) => refPlotCode(x) || x.producer_name)),
            ha: sumHa(forestRows.map(refOverlapHa)),
            pct: r.overlap_percent_forest_reserve,
            risk: highestRisk(forestRows.map((x) => refRisk(x, 'forest_reserve'))),
            error: r.error_forest_reserve,
          },
          {
            key: 'protected_area',
            icon: '■',
            label: 'Protected area',
            plots: uniqueCount(protectedRows.map((x) => refPlotCode(x) || x.producer_name)),
            ha: sumHa(protectedRows.map(refOverlapHa)),
            pct: r.overlap_percent_protected_area,
            risk: highestRisk(protectedRows.map((x) => refRisk(x, 'protected_area'))),
            error: r.error_protected_area,
          },
          {
            key: 'forest_cover',
            icon: '▲',
            label: 'Forest cover 2020',
            plots: uniqueCount(coverRows.map((x) => refPlotCode(x) || x.producer_name)),
            ha: sumHa(coverRows.map(refOverlapHa)),
            pct: r.overlap_percent_forest_cover,
            risk: highestRisk(coverRows.map((x) => refRisk(x, 'forest_cover'))),
            error: r.error_forest_cover,
          },
        ];

        const significantCount = pairs.filter((p) => pairSeverity(p) === 'Significant').length;
        const moderateCount = pairs.filter((p) => pairSeverity(p) === 'Moderate').length;
        const pairOverlapHa = sumHa(pairs.map((p) => p.overlap_area));
        const errors = [r.error_forest_reserve, r.error_protected_area, r.error_forest_cover].filter(Boolean);

        const summaryDot = (color: string) => (
          <span aria-hidden="true" style={{ display: 'inline-block', width: '4px', height: '4px', backgroundColor: color, borderRadius: '50%', marginRight: '12px' }}></span>
        );
        const valueStyle: React.CSSProperties = { fontSize: '1.25rem', fontWeight: 'bold', color: '#2d3748' };

        return (
        <div className="analysis-results">
          {/* Summary Table */}
          <div className="summary-section">
            <h2 style={{ color: '#2d3748', marginBottom: '1.5rem' }}>Analysis Summary</h2>
            <table className="summary-table">
              <thead>
                <tr>
                  <th scope="col">Metric</th>
                  <th scope="col">Value</th>
                </tr>
              </thead>
              <tbody>
                {plotCount !== undefined && (
                  <tr>
                    <td>{summaryDot('#718096')}Plots Analysed</td>
                    <td style={valueStyle}>{plotCount.toLocaleString()}</td>
                  </tr>
                )}
                {totalArea !== undefined && (
                  <tr>
                    <td>{summaryDot('#718096')}Total Plot Area (computed from geometry)</td>
                    <td style={valueStyle}>{totalArea.toFixed(2)} ha</td>
                  </tr>
                )}
                <tr>
                  <td>{summaryDot('#4299e1')}Plot-to-Plot Overlaps</td>
                  <td style={valueStyle}>
                    {r.total_overlap_count}
                    {touches.length > 0 && (
                      <span className="summary-note">
                        {' '}(+{touches.length} boundary touch{touches.length === 1 ? '' : 'es'} below {toleranceText} tolerance)
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>{summaryDot('#48bb78')}Overall Plot-to-Plot Overlap</td>
                  <td style={valueStyle}>{formatPercent(r.overall_overlap_percent)}</td>
                </tr>
                <tr>
                  <td>{summaryDot('#38a169')}Forest Reserve Overlap</td>
                  <td style={valueStyle}>{formatPercent(r.overlap_percent_forest_reserve)}</td>
                </tr>
                <tr>
                  <td>{summaryDot('#3182ce')}Protected Area Overlap</td>
                  <td style={valueStyle}>{formatPercent(r.overlap_percent_protected_area)}</td>
                </tr>
                {r.overlap_percent_forest_cover !== undefined && (
                  <tr>
                    <td>{summaryDot('#d69e2e')}Forest Cover Overlap (2020)</td>
                    <td style={valueStyle}>
                      {r.overlap_percent_forest_cover === null ? 'Not available' : formatPercent(r.overlap_percent_forest_cover)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {errors.length > 0 && (
              <div className="analysis-warning" role="status">
                <strong>⚠ Some checks could not be completed:</strong>
                <ul>
                  {errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
          </div>

          <MethodologyPanel methodology={r.methodology} />

          {/* Detailed Results */}
          <div className="results-sections">
            {/* Overlap summary by category */}
            <div className="results-section">
              <h3>📊 Overlap Summary by Category</h3>
              <p className="section-description">
                Percentages are relative to the total plot area{totalArea !== undefined ? ` (${totalArea.toFixed(2)} ha)` : ''}.
              </p>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">Category</th>
                      <th scope="col">Affected Plots</th>
                      <th scope="col">Total Overlap (ha)</th>
                      <th scope="col">% of Total Area</th>
                      <th scope="col">Highest Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryRows.map((c) => (
                      <tr key={c.key}>
                        <th scope="row" className="category-cell">
                          <div className="overlap-type-cell">
                            <span className="overlap-icon" aria-hidden="true">{c.icon}</span>
                            <span>{c.label}</span>
                          </div>
                        </th>
                        <td>{c.error ? '—' : c.plots}</td>
                        <td>{c.error ? '—' : formatHa(c.ha)}</td>
                        <td>{c.error || c.pct === null ? 'Not available' : formatPercent(c.pct)}</td>
                        <td>
                          {c.error ? (
                            <span className="muted">Check failed</span>
                          ) : c.risk ? (
                            <span className={riskClass(c.risk)}>{c.risk}</span>
                          ) : (
                            <span className="muted">No overlap</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Plot code search */}
            <div className="plot-filter">
              <label htmlFor="plot-filter-input">Find plot code</label>
              <input
                id="plot-filter-input"
                type="search"
                placeholder="e.g. TWF000090502"
                value={plotFilter}
                onChange={(e) => setPlotFilter(e.target.value)}
                aria-describedby="plot-filter-help"
              />
              {plotFilter && (
                <button type="button" className="btn secondary plot-filter-clear" onClick={() => setPlotFilter('')}>
                  Clear
                </button>
              )}
              <span id="plot-filter-help" className="plot-filter-help">
                Filters every table below. Use “Map” on a row to see that plot on the map.
              </span>
            </div>

            {/* Plot-to-Plot Overlaps Table */}
            {(pairs.length > 0 || touches.length > 0) && (
              <div className="results-section">
                <h3>● Plot-to-Plot Overlaps ({countLabel(shownPairs.length, pairs.length)})</h3>
                <p className="section-description">
                  These overlaps indicate potential boundary conflicts between different rubber plots that may require field verification.
                </p>
                {pairs.length === 0 ? (
                  <p className="empty-note">No plot-to-plot overlaps above the {toleranceText} tolerance.</p>
                ) : shownPairs.length === 0 ? (
                  <p className="empty-note">No overlaps match “{plotFilter}”.</p>
                ) : (
                  <PairTable rows={shownPairs} onShowMap={showOnMap} caption="Plot-to-plot overlaps" />
                )}

                {touches.length > 0 && (
                  <details className="boundary-touches" open>
                    <summary>
                      Boundary touches (below tolerance) — {countLabel(shownTouches.length, touches.length)}
                    </summary>
                    <p className="section-description">
                      {r.methodology?.tolerance_note ||
                        `Overlaps smaller than ${toleranceText} are treated as boundary touches and excluded from counts, percentages and risk.`}
                    </p>
                    {shownTouches.length === 0 ? (
                      <p className="empty-note">No boundary touches match “{plotFilter}”.</p>
                    ) : (
                      <PairTable rows={shownTouches} onShowMap={showOnMap} caption="Boundary touches below tolerance" />
                    )}
                  </details>
                )}

                {pairs.length > 0 && (
                  <>
                    <div className="overlap-summary">
                      <h4>Plot Overlap Summary</h4>
                      <div className="summary-stats">
                        <div className="stat-item">
                          <span className="stat-label">Total Overlapping Pairs:</span>
                          <span className="stat-value">{pairs.length}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Total Overlap Area:</span>
                          <span className="stat-value">{pairOverlapHa.toFixed(4)} ha</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Significant Overlaps:</span>
                          <span className="stat-value">{significantCount}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Average Overlap Size:</span>
                          <span className="stat-value">{(pairOverlapHa / pairs.length).toFixed(4)} ha</span>
                        </div>
                      </div>
                    </div>

                    <div className="overlap-recommendations">
                      <h4>📋 Recommended Actions</h4>
                      <div className="recommendations-list">
                        {significantCount > 0 && (
                          <div className="recommendation-item high-priority">
                            <span className="rec-icon" aria-hidden="true">🔴</span>
                            <div className="rec-content">
                              <strong>High Priority:</strong> {significantCount} significant overlaps require immediate field verification and boundary resolution.
                            </div>
                          </div>
                        )}
                        {moderateCount > 0 && (
                          <div className="recommendation-item medium-priority">
                            <span className="rec-icon" aria-hidden="true">🟡</span>
                            <div className="rec-content">
                              <strong>Medium Priority:</strong> {moderateCount} moderate overlaps should be reviewed for GPS accuracy and boundary clarification.
                            </div>
                          </div>
                        )}
                        <div className="recommendation-item general">
                          <span className="rec-icon" aria-hidden="true">ℹ️</span>
                          <div className="rec-content">
                            <strong>General:</strong> Consider implementing GPS surveying for affected plots and establish clear demarcation between adjacent plantations.
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Forest Reserve Overlaps Table */}
            {forestRows.length > 0 && (
              <div className="results-section">
                <h3>▬ Forest Reserve Overlaps ({countLabel(shownForest.length, forestRows.length)})</h3>
                {shownForest.length === 0 ? (
                  <p className="empty-note">No forest reserve overlaps match “{plotFilter}”.</p>
                ) : (
                  <RefTable rows={shownForest} category="forest_reserve" onShowMap={showOnMap} />
                )}
              </div>
            )}

            {/* Protected Area Overlaps Table */}
            {protectedRows.length > 0 && (
              <div className="results-section">
                <h3>■ Protected Area Overlaps ({countLabel(shownProtected.length, protectedRows.length)})</h3>
                {shownProtected.length === 0 ? (
                  <p className="empty-note">No protected area overlaps match “{plotFilter}”.</p>
                ) : (
                  <RefTable rows={shownProtected} category="protected_area" onShowMap={showOnMap} />
                )}
              </div>
            )}

            {/* Forest Cover Overlaps Table */}
            {(coverRows.length > 0 || noCoverage.length > 0) && (
              <div className="results-section">
                <h3>▲ Forest Cover Overlaps ({countLabel(shownCover.length, coverRows.length)})</h3>
                {noCoverage.length > 0 && (
                  <div className="analysis-warning" role="note">
                    <strong>⚠ {noCoverage.length} plot{noCoverage.length === 1 ? ' is' : 's are'} outside the forest-cover dataset coverage and {noCoverage.length === 1 ? 'was' : 'were'} not assessed:</strong>{' '}
                    {noCoverage.slice(0, 20).join(', ')}
                    {noCoverage.length > 20 && ` and ${noCoverage.length - 20} more`}
                  </div>
                )}
                {coverRows.length > 0 && (shownCover.length === 0 ? (
                  <p className="empty-note">No forest cover overlaps match “{plotFilter}”.</p>
                ) : (
                  <RefTable rows={shownCover} category="forest_cover" onShowMap={showOnMap} />
                ))}
              </div>
            )}
          </div>

          {/* Map Visualizations */}
          <div className="map-visualizations">
            <h3>Map Visualizations</h3>
            <p className="maps-description">
              Interactive maps showing different types of overlaps and analysis results.
            </p>

            {MAPS.map((m) => (
              <MapVisualization
                key={m.type}
                mapType={m.type}
                title={m.title}
                description={m.description}
                customerId={mapCustomerId}
                focus={mapFocus[m.type]}
                openRequest={mapOpenRequest[m.type]}
                onClearFocus={() => setMapFocus((prev) => ({ ...prev, [m.type]: undefined }))}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="analysis-actions">
            <button 
              onClick={() => window.location.href = `/reports?customer_id=${mapCustomerId}`}
              className="btn primary"
            >
              Generate Report
            </button>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default Analysis;